// ContentContext v1.0.6 - Stable React context with proper hydration
import React, { createContext, useState, useCallback, useEffect, useMemo, useContext } from 'react';
import type { ReactNode, ReactElement } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ContentItem {
  key: string;
  content: string;
  category: string;
  description?: string;
}

interface ContentContextType {
  content: Record<string, string>;
  loading: boolean;
  updateContent: (key: string, newContent: string) => Promise<void>;
  refreshContent: () => Promise<void>;
  getContent: (key: string, fallback?: string) => string;
}

const ContentContext = createContext<ContentContextType | undefined>(undefined);

export function ContentProvider({ children }: { children: ReactNode }): ReactElement {
  const [content, setContent] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const loadContent = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('app_content')
        .select('key, content');
      
      if (error) {
        setLoading(false);
        return;
      }
      
      const contentMap = data?.reduce((acc, item) => {
        acc[item.key] = item.content;
        return acc;
      }, {} as Record<string, string>) || {};
      
      setContent(contentMap);
    } catch {
      // Error loading content (non-critical)
    } finally {
      setLoading(false);
    }
  }, []);

  const updateContent = useCallback(async (key: string, newContent: string) => {
    try {
      const { error } = await supabase
        .from('app_content')
        .update({ content: newContent })
        .eq('key', key);
      
      if (error) throw error;
      
      setContent(prev => ({
        ...prev,
        [key]: newContent
      }));
    } catch (error) {
      throw error;
    }
  }, []);

  const getContent = useCallback((key: string, fallback: string = key) => {
    if (!content || typeof content !== 'object') return fallback;
    return content[key] || fallback;
  }, [content]);

  useEffect(() => {
    loadContent().catch(() => {
      setLoading(false);
    });

    const channel = supabase
      .channel('app_content_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'app_content'
        },
        (payload) => {
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            const newRecord = payload.new as ContentItem;
            setContent(prev => ({
              ...prev,
              [newRecord.key]: newRecord.content
            }));
          } else if (payload.eventType === 'DELETE') {
            const oldRecord = payload.old as ContentItem;
            setContent(prev => {
              const updated = { ...prev };
              delete updated[oldRecord.key];
              return updated;
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadContent]);

  const contextValue = useMemo(() => ({
    content,
    loading,
    updateContent,
    refreshContent: loadContent,
    getContent
  }), [content, loading, updateContent, loadContent, getContent]);

  return (
    <ContentContext.Provider value={contextValue}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent(): ContentContextType {
  const context = useContext(ContentContext);
  if (context === undefined) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
}

export function useContentText(key: string, fallback?: string): string {
  const { getContent } = useContent();
  return getContent(key, fallback);
}
